import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import useEnvStore from '@/store/modules/env';
import notificationStore, { ERROR } from '@/store/modules/notifications';

type CustomAxiosInstance = AxiosInstance & {
  sparkApi<T = any>(data: any, options?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  dashboardApi<T = any>(data: any, options?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
};

const axiosInstance: CustomAxiosInstance = axios.create({
  baseURL: '/dashboard-api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
}) as CustomAxiosInstance;

axiosInstance.interceptors.response.use(
  (response) => response,
  (err) => {
    handleApiError(err.response?.status, err.request?.responseURL);
    return Promise.reject(err);
  },
);

function handleApiError(status: number | undefined, responseURL?: string): void {
  if (
    status &&
    [401, 419].includes(status) &&
    (!responseURL || responseURL.indexOf('/user') === -1)
  ) {
    // todo there's got to be a better way of doing this! without circular dependencies a problem
    window.location.reload();
  }

  if (status && status >= 500) {
    notificationStore.notify(
      'Error',
      "Sorry an error has occurred, please try again. If you're still having issues, please contact our support team.",
      ERROR,
    );
  }
}

// Listen for API errors from other packages like the React forms-automations package
window.addEventListener('spark:api-error', (event: Event) => {
  const customEvent = event as CustomEvent<{ status?: number; url?: string }>;
  handleApiError(customEvent.detail.status, customEvent.detail.url);
});

// Simple helper function for the proxy
// The proxy provides authenticated access to the API via the spark-dash-api
axiosInstance.sparkApi = <T = any>(data: any, options?: AxiosRequestConfig) => {
  const envStore = useEnvStore();

  if (!envStore.siteId || !envStore.siteEnv) {
    return Promise.reject(
      new Error('siteId and siteEnv are required to call the sparkApi endpoint'),
    );
  }

  return axiosInstance.post<T>(`/${envStore.siteId}/${envStore.siteEnv}/proxy-api`, data, options);
};

axiosInstance.dashboardApi = <T = any>(data: any, options?: AxiosRequestConfig) => {
  const envStore = useEnvStore();

  if (!envStore.siteId || !envStore.siteEnv) {
    return Promise.reject(
      new Error('siteId and siteEnv are required to call the dashboardApi endpoint'),
    );
  }

  return axiosInstance.post<T>(`/${envStore.siteId}/${envStore.siteEnv}`, data, options);
};

export default axiosInstance;
